import React from "react";
import AddAthleteProfileForm, { AthleteProfile, AthleteSaveMeta } from "./AddAthleteProfileForm";
import { useAuth } from "../contexts/AuthContext";
import { buildApiUrl } from "../utils/apiConfig";
import { Guardian } from "../types/athletes";
import { resolveGuardianEmails } from "../utils/guardianEmails";
import "../styles/add-team-member-modal.css";

type Props = {
    open?: boolean;
    onClose: () => void;
    onSaved?: (saved: AthleteProfile, meta?: AthleteSaveMeta) => void;
    onProfileSaved?: () => void;
    /** When set (e.g. from /team/view/:userId), athlete is created as this team member's parent. */
    parentUserId?: string | number;
    initial?: Partial<AthleteProfile> & {
        id?: string | number;
        /** Auth user id — used for GET /api/athletes/add/view?userId= */
        userId?: string | number;
        guardians?: Guardian[];
        assignedStaffName?: string;
        assignedStaffId?: number;
        location?: string;
        guardianEmail?: string;
        parentEmail2?: string;
    };
    athleteData?: any;
};

export default function AddAthleteProfileModal({
    open = true,
    onClose,
    onSaved,
    onProfileSaved,
    parentUserId: parentUserIdProp,
    initial,
    athleteData
}: Props) {
    // Numeric user id from AuthContext (in-memory) is the source of truth for the create path.
    const { userInfo } = useAuth();

    if (!open) return null;

    // Profile id (for form state / update) — distinct from auth user id
    const athleteId = (initial as any)?.id || (initial as any)?.athleteId || athleteData?.id || athleteData?.athleteId || null;

    // GET /api/athletes/add/view?userId= expects the athlete's auth user id (see AthleteService.getAddAthleteView).
    // List rows often expose id/athleteId as athlete_profile PK; passing those as userId causes 404.
    const loadViewUserId =
        (initial as any)?.userId ??
        athleteData?.userId ??
        null;

    // Prepare initial data from athleteData or initial prop
    // Handle both staffName/staffId and assignedStaffName/assignedStaffId for compatibility
    let initialData;
    if (athleteData) {
        const guardianEmails = resolveGuardianEmails(athleteData);
        initialData = {
            id: athleteId || athleteData.id,
            firstName: athleteData.firstName || "",
            lastName: athleteData.lastName || "",
            email: athleteData.email || "",
            parentEmail: guardianEmails.parentEmail,
            parentEmail2: guardianEmails.parentEmail2,
            state: athleteData.location || athleteData.state || "",
            stateId: athleteData.stateId,
            staffName: athleteData.staffName || athleteData.assignedStaffName || "",
            staffId: athleteData.staffId || athleteData.assignedStaffId,
            sport: athleteData.sport,
            sportName: athleteData.sportName,
        };
    } else if (initial) {
        const guardianEmails = resolveGuardianEmails(initial);
        initialData = {
            ...initial,
            id: athleteId || initial.id,
            parentEmail: guardianEmails.parentEmail,
            parentEmail2: guardianEmails.parentEmail2,
            staffName: initial.staffName || initial.assignedStaffName || "",
            staffId: initial.staffId || initial.assignedStaffId,
            state: initial.state || initial.location || "",
            stateId: initial.stateId,
            sport: initial.sport,
            sportName: initial.sportName,
        };
    } else {
        initialData = undefined;
    }



    const loadUrl =
        loadViewUserId != null && String(loadViewUserId).trim() !== ""
            ? buildApiUrl(`api/athletes/add/view?userId=${encodeURIComponent(String(loadViewUserId))}`)
            : buildApiUrl("api/athletes/add/view");

    // Pass base URL for creates - the form will construct the correct PUT URL for updates internally.
    // When parentUserIdProp is set (team view page), create as that team member; otherwise use logged-in user.
    const parentUserId =
        parentUserIdProp != null && String(parentUserIdProp).trim() !== ""
            ? String(parentUserIdProp)
            : userInfo?.id != null
              ? String(userInfo.id)
              : localStorage.getItem("userId");
    const saveUrl = parentUserId
        ? buildApiUrl(`api/athletes/${parentUserId}`)
        : buildApiUrl("api/athletes");

    const handleSaved = (saved: AthleteProfile, meta?: AthleteSaveMeta) => {
        onSaved?.(saved, meta);
        onProfileSaved?.();
        onClose();
    };

    return (
        <div className="team-modal" role="dialog" aria-modal="true" aria-labelledby="athlete-modal-title">
            <div className="team-modal-card">
                <button className="team-modal-close" aria-label="Close" onClick={onClose}>×</button>
                <div className="team-modal-content">
                    <AddAthleteProfileForm
                        loadUrl={loadUrl}
                        saveUrl={saveUrl}
                        useCookies
                        initial={initialData}
                        onClose={onClose}
                        onSaved={handleSaved}
                    />
                </div>
            </div>
        </div>
    );
}
